import React from 'react'
import { Link } from 'react-router-dom'
import Accordion from 'react-bootstrap/Accordion'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { FaInfoCircle } from 'react-icons/fa'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import moment from 'moment'
import AccordionHeaderContent from './AccordionHeaderContent'
import PendingContractStatus from './ContractStatus/PendingContractStatus'
import SignedContractStatus from './ContractStatus/SignedContractStatus'
import RefusedContractStatus from './ContractStatus/RefusedContractStatus'
import CanceledContractStatus from './ContractStatus/CanceledContractStatus'
import { getStepState } from '../utils/getStepState'
import IOrder from '@interfaces/IOrder'
import IUser from '@interfaces/IUser'
import Edit from '@resources/imgs/edit.svg'
import '@resources/css/tcle-form.css'

const SweetAlert = withReactContent(Swal)

interface TcleFormData {
  matching_choice: string
  partner_name: string
  partner_email: string
}

interface AccordionTcleProps {
  stepProgress: number
  inProgressOrder: IOrder | null
  user: IUser | null
  form: TcleFormData
  isSubmitting: boolean
  isSubmitted: boolean
  onFormChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onFormSubmit: (e: React.FormEvent<HTMLFormElement>) => void
}

const AccordionTcle: React.FC<AccordionTcleProps> = ({
  stepProgress,
  inProgressOrder,
  user,
  form,
  isSubmitting,
  isSubmitted,
  onFormChange,
  onFormSubmit,
}) => {
  const [showVideoModal, setShowVideoModal] = React.useState(false)
  const stepState = getStepState(stepProgress)
  const isLocked = isSubmitted || stepProgress === 100
  const donorId = inProgressOrder?.first_donor?.internal_id ?? ''
  const hasPartnerName = !!user?.patient?.partner_name
  const hasPartnerEmail = !!user?.patient?.partner_email
  const formattedDate = isSubmitted && inProgressOrder?.tcle_accepted_at
    ? moment(inProgressOrder.tcle_accepted_at).format('DD/MM/YYYY')
    : moment().format('DD/MM/YYYY')

  const fullDonor = `${inProgressOrder?.first_donor?.internal_id ?? ''}${inProgressOrder?.first_donor?.nickname ? ` - ${inProgressOrder.first_donor.nickname}` : ''}`

  const confirmMatching = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    SweetAlert.fire({
      title: 'Confirmação de Matching Genético',
      html: `
        <p class="body-lead">Estou ciente de que para realizar o matching genético com o doador, preciso:</p>
        <ul class="reqs">
          <li><span class="ic">✓</span><span>Fazer o <b>mapeamento genético da fonte de óvulos</b>, por meio de um exame de saliva adicional</span></li>
          <li><span class="ic">✓</span><span>Arcar com o custo aproximado de <b>R$ 2.900,00</b></span></li>
          <li><span class="ic">✓</span><span>Aguardar cerca de <b>30 dias úteis</b> para o resultado</span></li>
        </ul>
      `,
      input: 'checkbox',
      inputValue: 0,
      inputPlaceholder: 'Estou ciente e desejo prosseguir.',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      inputValidator: (result) => (!result ? 'É necessário confirmar que está ciente para prosseguir.' : null),
      customClass: { popup: 'tcle-a' },
    }).then(({ value: confirmed }) => {
      if (confirmed) {
        onFormChange({ target: { name, type: 'radio', value, checked: true } } as React.ChangeEvent<HTMLInputElement>)
      }
    })
  }

  const renderTcleContractStatus = () => {
    const contract = inProgressOrder?.tcle_contract
    if (!contract) {
      return null
    }

    const {
      status,
      sent_at,
      signed_at,
      refused_at,
      canceled_at,
      signing_url,
      document_url,
      has_patient_signed,
      total_signers,
      signed_count,
      are_all_signed,
    } = contract

    switch (status) {
    case 'pending':
    case 'sent':
      return (
        <PendingContractStatus
          internal_id={inProgressOrder?.internal_id}
          sent_at={sent_at}
          signing_url={signing_url}
          has_patient_signed={has_patient_signed}
          signed_count={signed_count}
          total_signers={total_signers}
          contract_holder="patient"
          full_donor={fullDonor}
        />
      )

    case 'signed':
      return (
        <SignedContractStatus
          signed_at={signed_at}
          document_url={document_url}
          has_patient_signed={has_patient_signed}
          signed_count={signed_count}
          total_signers={total_signers}
          are_all_signed={are_all_signed}
          signing_url={signing_url}
        />
      )

    case 'refused':
      return <RefusedContractStatus refused_at={refused_at} />

    case 'canceled':
      return <CanceledContractStatus canceled_at={canceled_at} />

    default:
      return null
    }
  }

  return (
    <Accordion.Item eventKey="tcle" className={`${stepState}-state`}>
      <Accordion.Header>
        <AccordionHeaderContent
          stepState={stepState}
          progress={stepProgress}
          title="Termo de Consentimento Livre Esclarecido (TCLE)"
        />
      </Accordion.Header>

      <Accordion.Body>
        <div className="check-sample p-3">
          <div className="select-payment">
            <div className="row">
              <div className="col-12 col-lg-12">
                <div className="box tcle-box">
                  <div className="tcle-form-head">
                    <div className="tcle-form-head-top">
                      {!isLocked && (
                        <Link className="tcle-edit-btn" to="/perfil" target="_blank" rel="noopener noreferrer">
                          <img src={Edit} alt="" />
                          <span className="ml-2">Editar</span>
                        </Link>
                      )}
                      <small className="required-note">* Campos obrigatórios</small>
                    </div>
                    <p className="tcle-intro">Preencha os dados abaixo para emitir o Termo de Consentimento Livre Esclarecido (TCLE) necessário para reserva de amostras do doador <span className="text-secondary">ID {donorId}</span>:</p>
                  </div>

                  <form className="tcle-form" onSubmit={onFormSubmit}>

                    <div className="row">
                      <div className="col-12 col-lg-6">
                        <div className="form-group">
                          <label>
                            <b>Nome Completo (de quem irá gestar)</b> <span className="text-danger ml-1">*</span>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id="tcle-name-tooltip">Nome de quem irá gestar — titular do Termo.</Tooltip>}
                            >
                              <span className="info-icon"><FaInfoCircle /></span>
                            </OverlayTrigger>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={user?.name ?? ''}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-12 col-lg-6">
                        <div className="form-group">
                          <label><b>E-mail (de quem irá gestar)</b> <span className="text-danger ml-1">*</span></label>
                          <input
                            type="email"
                            className="form-control"
                            value={user?.email ?? ''}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="col-12 col-lg-6">
                        <div className="form-group">
                          <label>
                            <b>Nome do Cônjuge/ Companheira(o), se houver</b>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id="tcle-partner-name-tooltip">Preencha se houver cônjuge/companheiro(a).</Tooltip>}
                            >
                              <span className="info-icon"><FaInfoCircle /></span>
                            </OverlayTrigger>
                          </label>
                          <input
                            type="text"
                            name="partner_name"
                            className="form-control"
                            value={hasPartnerName ? user?.patient?.partner_name ?? '' : form.partner_name}
                            onChange={onFormChange}
                            readOnly={hasPartnerName || isLocked}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-lg-6">
                        <div className="form-group">
                          <label><b>E-mail do Cônjuge/ Companheira(o), se houver e caso queira assinar o Termo</b></label>
                          <input
                            type="email"
                            name="partner_email"
                            className="form-control"
                            value={hasPartnerEmail ? user?.patient?.partner_email ?? '' : form.partner_email}
                            onChange={onFormChange}
                            readOnly={hasPartnerEmail || isLocked}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="genetics-intro-block genetics-intro-block--cta">
                      <p>
                        É normal que doadores, como todas as pessoas, sejam portadores <b>saudáveis</b> de variantes genéticas! Entenda sobre o mapeamento genético expandido dos nossos doadores e a decisão de fazer o matching genético.
                      </p>
                      <button
                        type="button"
                        className="tcle-submit"
                        onClick={() => setShowVideoModal(true)}
                      >
                        Entenda mais
                      </button>
                      {showVideoModal && (
                        <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center'}} onClick={() => setShowVideoModal(false)}>
                          <div style={{position: 'relative', width: 'min(1280px, 90vw)'}} onClick={(e) => e.stopPropagation()}>
                            <div style={{position: 'relative', paddingBottom: '56.25%', height: 0}}>
                              <iframe style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none'}} src="https://www.youtube.com/embed/tValnvIalmk" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen></iframe>
                            </div>
                            <button style={{position: 'absolute', top: '-10px', right: '-10px', background: 'red', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer'}} onClick={() => setShowVideoModal(false)}>×</button>
                          </div>
                        </div>
                      )}
                    </div>

                    <h5 className="tcle-section-title">Decisão sobre a realização do matching genético com o doador <span className="text-secondary">ID {donorId}</span>:</h5>

                    <div className="tcle-option">
                      <div className="tcle-option-title">
                        Realizar matching
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id="tcle-matching-tooltip">O mapeamento do doador é só metade da informação; o matching compara com a fonte de óvulos.</Tooltip>}
                        >
                          <span className="info-icon"><FaInfoCircle /></span>
                        </OverlayTrigger>
                      </div>
                      <label className="tcle-option-choice">
                        <input
                          type="radio"
                          name="matching_choice"
                          value="matching"
                          checked={form.matching_choice === 'matching'}
                          onChange={confirmMatching}
                          required
                          disabled={isLocked}
                        />
                        <span className="tcle-option-desc">Decidi <b>REALIZAR</b> o mapeamento genético da fonte de óvulos (próprios, da minha companheira ou doadora de óvulos) para a realização do teste de compatibilidade genética (matching) com o doador escolhido e confirmação de que a fonte de óvulos não é portadora de variantes ligadas ao mesmo gene identificada(s) no doador!</span>
                      </label>
                    </div>

                    <div className="tcle-option">
                      <div className="tcle-option-title">
                        Prosseguir sem matching
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id="tcle-no-matching-tooltip">Você declara ciência dos riscos de prosseguir sem o teste de compatibilidade.</Tooltip>}
                        >
                          <span className="info-icon"><FaInfoCircle /></span>
                        </OverlayTrigger>
                      </div>
                      <label className="tcle-option-choice">
                        <input
                          type="radio"
                          name="matching_choice"
                          value="no_matching"
                          checked={form.matching_choice === 'no_matching'}
                          onChange={onFormChange}
                          required
                          disabled={isLocked}
                        />
                        <span className="tcle-option-desc">Decidi <b>NÃO REALIZAR</b> a análise de compatibilidade genética (matching) entre o doador escolhido e a fonte de óvulos, mesmo depois de informada dos riscos implicados.</span>
                      </label>
                    </div>

                    <p className="tcle-footer">Após preenchimento, você receberá um e-mail da plataforma ClickSign para assinatura digital do Termo! A confirmação da reserva de amostra do doador só acontecerá após assinatura desse TCLE!</p>

                    {!isLocked && (
                      <button
                        type="submit"
                        className="tcle-submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Enviando...' : 'Enviar'}
                      </button>
                    )}
                  </form>

                  {isLocked && inProgressOrder?.tcle_contract && (
                    <div className="tcle-signature-status mt-4">
                      {renderTcleContractStatus()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  )
}

export default AccordionTcle
